/**
 * 組織課題（看板）の形。ボード・課題・列・Next・コメント・履歴と、既定値・上限・エラーの種類。
 *
 * **人は全て `users.id`（Google Workspace ID）で持つ。** メールは改名で変わり、ニックネームは
 * 表示用なので保存に使わない。表示名は読み出し時に入口が `users` から引く。
 *
 * **状態は列（`columnId`）で持ち、意味は列の `category` で決める。** 集計・完了扱い・`closedAt` の
 * 付与は `category` だけを見る。列名で判定すると、ボードごとに列を足したときに判定がぶれる。
 *
 * 日時は ISO 8601 の文字列で持つ（Firestore の `Timestamp` に依存させない。並べ替えも文字列比較で済む）。
 *
 * @graph-stack org-issues
 * @graph-domain Engineering
 * @graph-business Shapes, defaults, limits and error kinds of organization issue boards
 * @graph-connects none
 */
/** 優先度。未設定は `null`。 @graph-connects none */
export const PRIORITIES = ['high', 'medium', 'low'];
/**
 * 課題の共有状態。`draft` / `unshared` はボードの公開範囲より**狭める方向にだけ**効く
 * （作成者・担当・ボードの編集者だけが見られる）。
 * @graph-connects none
 */
export const SHARINGS = ['shared', 'draft', 'unshared'];
/** 列の意味。判定は全てこれで行う。 @graph-connects none */
export const COLUMN_CATEGORIES = ['todo', 'doing', 'paused', 'done', 'dropped'];
/** 課題を「閉じた」とみなす列の意味。`closedAt` はここへ入ったときに付く。 @graph-connects none */
export const CLOSED_CATEGORIES = ['done', 'dropped'];
/**
 * 既定の列（全ボード共通。作成時に入る）。
 * @graph-connects none
 */
export const DEFAULT_COLUMNS = [
    { id: 'backlog', label: '未着手', category: 'todo', position: 0, isDefault: true },
    { id: 'in_progress', label: '対応中', category: 'doing', position: 1, isDefault: true },
    { id: 'on_hold', label: '保留', category: 'paused', position: 2, isDefault: true },
    { id: 'review', label: '確認待ち', category: 'doing', position: 3, isDefault: true },
    { id: 'done', label: '完了', category: 'done', position: 4, isDefault: true },
    { id: 'dropped', label: '見送り', category: 'dropped', position: 5, isDefault: true },
];
/**
 * 課題作成時の本文の初期値（Notion の TE課題 / PDG組織課題 / PI Div課題で共通だった形）。
 * Next は本文ではなく `actions` に持つ（担当・期日・完了を機械が読めるようにするため）。
 * @graph-connects none
 */
export const DEFAULT_BODY_TEMPLATE = [
    '## 背景',
    '',
    '## 解決したいこと（課題・目的）',
    '',
    '## 解決策',
    '',
].join('\n');
/** 課題からのリンクの種類。 @graph-connects none */
export const LINK_KINDS = ['team_meeting_post', 'pilab_request', 'pull_request', 'url'];
/** 履歴に残す項目。 @graph-connects none */
export const HISTORY_FIELDS = [
    'created',
    'column',
    'priority',
    'assignees',
    'sharing',
    'pinned',
];
/**
 * 文字数・件数の上限。**入口に数値を写さない**（API が返す文言をそのまま見せる）。
 * @graph-connects none
 */
export const LIMITS = {
    boardName: 80,
    bodyTemplate: 5_000,
    columns: 20,
    columnLabel: 40,
    editors: 50,
    title: 200,
    body: 20_000,
    assignees: 20,
    actions: 50,
    actionText: 500,
    labels: 20,
    label: 40,
    links: 30,
    url: 2_000,
    linkTitle: 200,
    resolutionNote: 2_000,
    comment: 5_000,
    importBatch: 200,
};
/**
 * 入力が不正（400）。文言はそのまま画面・MCP に出す。
 * @graph-connects none
 */
export class OrgIssueValidationError extends Error {
    /** @graph-connects none */
    constructor(errors) {
        super(errors.join(' / '));
        this.name = 'OrgIssueValidationError';
    }
}
/**
 * 権限が無い（403）。入力を直しても通らないことを伝えるため、検証エラーと分ける。
 * @graph-connects none
 */
export class OrgIssuePermissionError extends Error {
    /** @graph-connects none */
    constructor(message) {
        super(message);
        this.name = 'OrgIssuePermissionError';
    }
}
/**
 * 見つからない（404）。**見られないものも「見つからない」にする**（存在を漏らさない）。
 * @graph-connects none
 */
export class OrgIssueNotFoundError extends Error {
    /** @graph-connects none */
    constructor(message) {
        super(message);
        this.name = 'OrgIssueNotFoundError';
    }
}
//# sourceMappingURL=model.js.map